import React from "react";
import { CheckCircle2 } from "lucide-react";
import { useSEO } from "@/lib/seo";
import { useEngagementModels, usePageContent } from "@/lib/content";
import { StarMotif } from "@/components/common/StarMotif";
import { CTABlock } from "@/components/common/CTABlock";

export default function EngagementModels() {
    useSEO({
        title: "Engagement Models | RKS Systems",
        description: "Flexible engagement models with the same senior engineering and transparent governance.",
    });
    const pc = usePageContent("engagement-models");
    const { items: ENGAGEMENT_MODELS } = useEngagementModels();
    return (
        <div data-testid="page-engagement">
            <section className="bg-[hsl(222_47%_7%)] text-white noise">
                <div className="container-app py-24 lg:py-32">
                    <div className="max-w-3xl">
                        <span className="overline text-primary">Engagement models</span>
                        <h1 className="mt-4 font-heading text-5xl sm:text-6xl font-extrabold tracking-tight leading-[1.05]">
                            {pc?.hero_headline || "Flexible Software Engineering Engagement Models for Enterprises"}
                        </h1>
                        <p className="mt-6 text-lg text-white/70 leading-relaxed">
                            {pc?.hero_subheadline || "Scale your software delivery with predictable, high-impact offshore talent. Choose from Fixed-Price Milestone Delivery for outcome-based projects, Time & Materials (T&M) for flexible agile pods, or Dedicated Engineering Teams for long-term capacity."}
                        </p>
                    </div>
                </div>
            </section>

            <section className="py-20 lg:py-28">
                <div className="container-app">
                    <div className="grid lg:grid-cols-2 gap-6">
                        {ENGAGEMENT_MODELS.map((e) => (
                            <div key={e.slug} id={e.slug} data-testid={`engagement-detail-${e.slug}`} className="relative overflow-hidden rounded-2xl border border-border bg-card p-8 lg:p-10 scroll-mt-24">
                                <div className="absolute -right-10 -top-10 opacity-[0.05]">
                                    <StarMotif className="h-40 w-40 text-primary" label="RKS" labelColor="#ffffff" />
                                </div>
                                <span className="overline">{e.name}</span>
                                <h2 className="mt-3 font-heading text-3xl font-extrabold">{e.tagline}</h2>
                                <p className="mt-4 text-muted-foreground leading-relaxed">{e.overview}</p>
                                {(e.benefits || []).length > 0 && (
                                    <div className="mt-8">
                                        <h3 className="font-heading font-semibold text-sm uppercase tracking-widest text-muted-foreground">Benefits</h3>
                                        <ul className="mt-3 space-y-2.5">
                                            {e.benefits.map((b) => (
                                                <li key={b} className="flex items-start gap-2.5 text-sm">
                                                    <CheckCircle2 className="h-4 w-4 mt-0.5 text-primary flex-shrink-0" strokeWidth={1.75} />
                                                    <span>{b}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                {(e.governance || []).length > 0 && (
                                    <div className="mt-7">
                                        <h3 className="font-heading font-semibold text-sm uppercase tracking-widest text-muted-foreground">Governance</h3>
                                        <ul className="mt-3 space-y-2.5">
                                            {e.governance.map((g) => (
                                                <li key={g} className="flex items-start gap-2.5 text-sm">
                                                    <StarMotif className="h-3.5 w-3.5 mt-1 text-primary flex-shrink-0" />
                                                    <span>{g}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                {(e.lifecycle || []).length > 0 && (
                                    <div className="mt-7">
                                        <h3 className="font-heading font-semibold text-sm uppercase tracking-widest text-muted-foreground">Delivery lifecycle</h3>
                                        <ol className="mt-3 space-y-2 text-sm text-foreground/80 list-decimal list-inside">
                                            {e.lifecycle.map((l) => (<li key={l}>{l}</li>))}
                                        </ol>
                                    </div>
                                )}
                                {(e.reporting || []).length > 0 && (
                                    <div className="mt-7">
                                        <h3 className="font-heading font-semibold text-sm uppercase tracking-widest text-muted-foreground">Reporting</h3>
                                        <ul className="mt-3 space-y-2 text-sm text-foreground/80 list-disc list-inside">
                                            {e.reporting.map((r) => (<li key={r}>{r}</li>))}
                                        </ul>
                                    </div>
                                )}
                                {e.cost_visibility && (
                                    <div className="mt-8 rounded-lg bg-secondary/60 px-5 py-4">
                                        <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Cost visibility</div>
                                        <div className="mt-1.5 text-sm text-foreground/80">{e.cost_visibility}</div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </section>
            <CTABlock title="Not sure which model fits?" description="Book a 30-minute session with a delivery leader. We'll help you pick the right one." />
        </div>
    );
}
